import { api, download } from '@/api/client'
import type { DeliveryWindow, ProductUnit } from '@/api/suppliers'

export type OrderStatus =
  | 'draft'
  | 'sent'
  | 'received'
  | 'confirmed'
  | 'partially_confirmed'
  | 'delivered'
  | 'cancelled'

export type DeliverySlot = 'morning' | 'noon' | 'afternoon' | 'custom'

export interface CartItem {
  id: string
  supplierId: string
  businessItemId: string | null
  supplierProductId: string | null
  name: string
  unit: ProductUnit
  quantity: string
  note: string | null
  unavailableUntil: string | null
  isAvailableToday: boolean
}

export interface CartGroup {
  supplierId: string
  supplierName: string
  supplierEmail: string
  items: CartItem[]
  delivery: DeliveryWindow | null
}

export interface Cart {
  id: string
  groups: CartGroup[]
  itemCount: number
  /** Πόσες ξεχωριστές παραγγελίες θα σταλούν. */
  orderCount: number
  /** Γραμμές που ο προμηθευτής έχει δηλώσει σε έλλειψη σήμερα. */
  unavailableCount: number
  /** Η νωρίτερη ημέρα που εξυπηρετεί όλους τους προμηθευτές του καλαθιού. */
  earliestDeliveryDate: string | null
}

export interface CheckoutOrder {
  id: string
  orderNumber: string
  supplierId: string
  supplierName: string
  itemCount: number
}

export interface OrderListItem {
  id: string
  orderNumber: string
  status: OrderStatus
  supplierId: string
  supplierName: string
  /** Η άλλη πλευρά για τον προμηθευτή. */
  businessName: string
  itemCount: number
  requestedDeliveryDate: string | null
  createdAt: string
  sentAt: string | null
}

export interface OrderItem {
  id: string
  name: string
  unit: ProductUnit
  quantity: string
  confirmedQuantity: string | null
  lineNote: string | null
  sortOrder: number
}

export interface OrderEvent {
  id: string
  eventType: string
  actorType: string
  payload: Record<string, unknown>
  createdAt: string
}

export interface Order {
  id: string
  orderNumber: string
  status: OrderStatus
  supplierId: string
  supplierName: string
  supplierEmail: string
  businessName: string
  businessPhone: string | null
  businessAddress: string | null
  requestedDeliveryDate: string | null
  requestedDeliverySlot: DeliverySlot | null
  requestedDeliveryTime: string | null
  orderNote: string | null
  cancelReason: string | null
  createdAt: string
  sentAt: string | null
  receivedAt: string | null
  confirmedAt: string | null
  deliveredAt: string | null
  cancelledAt: string | null
  hasModifiedQuantities: boolean
  items: OrderItem[]
  events: OrderEvent[]
}

export interface Page<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export const cartApi = {
  get: () => api.get<Cart>('/cart'),
  addItem: (payload: {
    supplierId: string
    businessItemId?: string
    supplierProductId?: string
    name?: string
    unit?: ProductUnit
    quantity: string
    note?: string
  }) => api.post<Cart>('/cart/items', payload),
  updateItem: (itemId: string, payload: { quantity?: string; note?: string }) =>
    api.patch<Cart>(`/cart/items/${itemId}`, payload),
  removeItem: (itemId: string) => api.delete<Cart>(`/cart/items/${itemId}`),
  /** Μεταφέρει γραμμή σε άλλον προμηθευτή που έχει το ίδιο είδος. */
  moveItem: (itemId: string, businessItemId: string) =>
    api.put<Cart>(`/cart/items/${itemId}/supplier`, { businessItemId }),
  clear: () => api.delete<Cart>('/cart'),
  checkout: (payload: {
    requestedDeliveryDate?: string
    requestedDeliverySlot?: DeliverySlot
    requestedDeliveryTime?: string
    orderNote?: string
    /** Μόνο αυτοί οι προμηθευτές· οι υπόλοιποι μένουν στο καλάθι. */
    supplierIds?: string[]
  }) => api.post<{ orders: CheckoutOrder[] }>('/cart/checkout', payload),
}

export const ordersApi = {
  list: (
    params: {
      status?: OrderStatus
      open?: boolean
      awaiting?: boolean
      deliveryDate?: string
      q?: string
      page?: number
    } = {},
  ) => {
    const query = new URLSearchParams()
    if (params.status) query.set('status', params.status)
    if (params.open) query.set('open', '1')
    if (params.awaiting) query.set('awaiting', '1')
    if (params.deliveryDate) query.set('deliveryDate', params.deliveryDate)
    if (params.q) query.set('q', params.q)
    if (params.page) query.set('page', String(params.page))
    const suffix = query.toString()
    return api.get<Page<OrderListItem>>(`/orders${suffix ? `?${suffix}` : ''}`)
  },
  get: (id: string) => api.get<Order>(`/orders/${id}`),
  cancel: (id: string, reason?: string) => api.post<Order>(`/orders/${id}/cancel`, { reason }),
  approveChanges: (id: string) => api.post<Order>(`/orders/${id}/approve-changes`),
  duplicate: (id: string) =>
    api.post<{ addedItems: number; supplierId: string }>(`/orders/${id}/duplicate`),
  downloadPdf: (id: string, orderNumber: string) =>
    download(`/orders/${id}/pdf`, `${orderNumber}.pdf`),
  // Πλευρά προμηθευτή: ίδιες απαντήσεις με τον σύνδεσμο του email, με λογαριασμό.
  respondConfirm: (id: string, note?: string) =>
    api.post<Order>(`/supplier/orders/${id}/confirm`, { note }),
  respondPartial: (id: string, lines: { itemId: string; confirmedQuantity: string }[], note?: string) =>
    api.post<Order>(`/supplier/orders/${id}/partial`, { lines, note }),
  markDelivered: (id: string) => api.post<Order>(`/supplier/orders/${id}/delivered`),
}
